import React, { useState } from 'react';
import { useQuery, useMutation } from 'react-apollo-hooks';
import { useTranslation } from 'react-i18next';
import { loader } from 'graphql.macro';
import cn from 'classnames';

import { WithTitle, Button, Form, Field, Select, Modal, Radio } from '@loyalup/ui';
import onlyData from 'utils/onlyData';

import { ReactComponent as EditIcon } from 'icons/edit.svg';
import { ReactComponent as DelIcon } from 'icons/del.svg';

import s from './Loyalty.module.css';

const LOYALTY_PROGRAMS_QUERY = loader('../../gql/queries/LoyaltyPrograms.gql');
const UPDATE_LOYALTY_PROGRAM_MUTATION = loader('../../gql/mutations/UpdateLoyaltyProgram.gql');
const CREATE_LOYALTY_PROGRAM_MUTATION = loader('../../gql/mutations/CreateLoyaltyProgram.gql');
const DELETE_LOYALTY_PROGRAM_MUTATION = loader('../../gql/mutations/DeleteLoyaltyProgram.gql');

const percentLimiter = num => {
  if (num === '') return '';
  return Math.min(Math.abs(num), 100);
};

const NthOrderForm = ({ type, config, setField, onSave, loading }) => {
  const [t] = useTranslation();

  const { name, nthCount, useInCurrentOrder, useSeveralTimes, beginDate, endDate, temporary } = config || {};

  return (
    <>
      <Field
        className={s.formItem}
        value={name}
        type="text"
        label={t('Название программы')}
        onChange={name => setField({ name })}
        autoFocus
        required
      />
      <Field
        className={s.formItem}
        label={t('Покупок до подарка')}
        type="number"
        value={nthCount}
        onChange={nthCount => setField({ nthCount })}
        required
      />
      <label className={cn(s.column, s.formItem)}>
        {t('Когда выдавать вознаграждение')}
        <Radio
          className={s.radioLabel}
          onChange={() => setField({ useInCurrentOrder: true })}
          checked={!!useInCurrentOrder}
        >
          {t('С этим заказом')}
        </Radio>
        <Radio
          className={s.radioLabel}
          onChange={() => setField({ useInCurrentOrder: false })}
          checked={!useInCurrentOrder}
        >
          {t('Со следующим заказом')}
        </Radio>
      </label>
      <label className={cn(s.column, s.formItem)}>
        {t('Клиент участвует в программе несколько раз?')}
        <Radio
          className={s.radioLabel}
          onChange={() => setField({ useSeveralTimes: true })}
          checked={!!useSeveralTimes}
        >
          {t('Да')}
        </Radio>
        <Radio
          className={s.radioLabel}
          onChange={() => setField({ useSeveralTimes: false })}
          checked={!useSeveralTimes}
        >
          {t('Нет')}
        </Radio>
      </label>
      <label className={cn(s.column, s.formItem)}>
        {t('В течение какого периода?')}
        <Radio className={s.radioLabel} onChange={() => setField({ temporary: true })} checked={temporary}>
          {t('Срока действия программы')}
        </Radio>
        <Radio className={s.radioLabel} onChange={() => setField({ temporary: false })} checked={!temporary}>
          {t('Бессрочно')}
        </Radio>
      </label>
      <Field
        className={s.formItem}
        label={t('Начало программы')}
        disabled={!temporary}
        type="date"
        value={beginDate}
        onChange={beginDate => setField({ beginDate })}
        required={temporary}
      />
      <Field
        className={s.formItem}
        label={t('Конец программы')}
        disabled={!temporary}
        type="date"
        value={endDate}
        onChange={endDate => setField({ endDate })}
        required={temporary}
      />
    </>
  );
};

const ProgramForm = ({ type, config, setField, onSave, amountFormTitle, percentFormatter, loading }) => {
  const [t] = useTranslation();

  const { amount, period, expiration, after } = config || {};

  const DEFAULT_EXPIRATION = 365;

  return (
    <>
      <Field
        className={s.formItem}
        label={amountFormTitle}
        type="number"
        value={amount}
        onChange={value => {
          const amount = percentFormatter ? percentLimiter(value) : value;
          setField({ amount });
        }}
        autoFocus
        required
      />
      {period !== undefined && (
        <Field
          label={t('Действуют дней')}
          className={s.formItem}
          type="number"
          value={period}
          onChange={period => {
            setField({ period });
          }}
          required
        />
      )}
      {expiration !== undefined && (
        <label className={cn(s.column, s.formItem)}>
          {t('В течение какого периода?')}
          <Radio
            className={s.radioLabel}
            onChange={value => value && setField({ expiration: 0 })}
            checked={expiration === 0}
          >
            {t('Бессрочно')}
          </Radio>
          <Radio
            className={s.radioLabel}
            onChange={value => value && setField({ expiration: DEFAULT_EXPIRATION })}
            checked={expiration > 0}
          >
            {t('Срока действия программы')}
          </Radio>
          <Field
            label={t('Действуют дней')}
            disabled={expiration === 0}
            className={s.formItem}
            type="number"
            value={expiration}
            onChange={expiration => {
              setField({ expiration: expiration || 1 });
            }}
            required={expiration}
          />
        </label>
      )}
      {after !== undefined && (
        <Field
          label={t('Через сколько дней начисляются бонусы')}
          className={s.formItem}
          type="number"
          name="after"
          value={after}
          onChange={after => {
            setField({ after });
          }}
          required
        />
      )}
    </>
  );
};

const Program = ({
  type,
  name,
  config,
  serialize,
  title,
  description,
  amountTitle,
  amountFormTitle,
  expirationTitle,
  renderForm,
  setEdit,
  percentFormatter,
}) => {
  const [t] = useTranslation();

  const [confirmCancel, showConfirmCancel] = useState(false);

  const isDefined = value => value !== undefined;

  const [deleteLoyaltyProgram, { loading }] = useMutation(DELETE_LOYALTY_PROGRAM_MUTATION, {
    refetchQueries: [{ query: LOYALTY_PROGRAMS_QUERY }],
  });

  const onDelete = async () => {
    const { loyaltyType, id } = serialize(config);
    await deleteLoyaltyProgram({ variables: { loyaltyType, id } });
    showConfirmCancel(false);
  };

  return (
    <div className={cn(s.item, s.enabledItem)}>
      <span className={cn(s.top, s.row, s.title)}>
        {title}
        {name && ` (${name})`}
      </span>
      <p>{description}</p>
      <div
        className={cn(s.settings, {
          [s.spaceBetween]: isDefined(config?.expiration) && isDefined(config?.after),
        })}
      >
        <span className={s.summary}>
          <span>{amountTitle}</span>
          {isDefined(config?.period) && (
            <span>
              {t('Скидка может быть использована в течение {{count, number}} дней', { count: +config.period })}
            </span>
          )}
          {isDefined(config?.expiration) && (
            <span>
              {config.expiration
                ? t('Срок действия бонусов: {{count, number}} дней', { count: +config.expiration })
                : t('Срок действия бонусов не ограничен')}
            </span>
          )}
          {isDefined(config?.after) && (
            <span>{t('Бонусы начисляются через {{count, number}} дней', { count: +config.after })}</span>
          )}
        </span>
        {type === 'NTH_ORDER_FREE' && (
          <span className={s.summary}>
            <span>
              {config.temporary
                ? t('Программа действует с {{beginDate, date}} по {{endDate, date}}', {
                    beginDate: config.beginDate,
                    endDate: config.endDate,
                  })
                : t('Программа действует бессрочно')}
            </span>
            <span>{config.useSeveralTimes ? t('Применяется множество раз') : t('Применяется один раз')}</span>
            <span>
              {config.useInCurrentOrder
                ? t('Подарок выдается в том же чеке, что и последняя покупка')
                : t('Подарок выдается в следующем чеке')}
            </span>
          </span>
        )}
        <div className={s.buttons}>
          <Button
            theme="grey"
            icons="first"
            onClick={() => setEdit({ title, config, amountFormTitle, percentFormatter, serialize, type })}
          >
            <EditIcon />
            {t('Изменить')}
          </Button>
          <Button icons="first" theme="grey" onClick={() => showConfirmCancel(true)}>
            <DelIcon />
            {t('Отключить')}
          </Button>
        </div>
      </div>
      <Modal confirm isOpen={confirmCancel} onClose={() => showConfirmCancel(false)}>
        <Form onSubmit={onDelete}>
          <p>{t('Вы уверены, что хотите отключить программу: {{title}}?', { title })}</p>
          <div>
            <Button type="submit" theme="green" loading={loading} center>
              {t('Отключить')}
            </Button>
            <Button
              theme="black"
              onClick={() => {
                showConfirmCancel(false);
              }}
            >
              {t('Отмена')}
            </Button>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

const Loyalty = () => {
  const [t] = useTranslation();
  const [edit, setEdit] = useState(null);
  const [loading, setLoading] = useState(false);

  const { data } = useQuery(LOYALTY_PROGRAMS_QUERY, {
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  const [updateProgram] = useMutation(UPDATE_LOYALTY_PROGRAM_MUTATION);
  const [createProgram] = useMutation(CREATE_LOYALTY_PROGRAM_MUTATION);

  const onSave = async () => {
    setLoading(true);
    const { config, serialize } = edit;
    const getVariables = () => {
      const { id, ...data } = serialize(config);
      return id ? { id, ...data } : data;
    };
    const mutation = getVariables().id ? updateProgram : createProgram;
    try {
      await mutation({
        variables: getVariables(),
        refetchQueries: [{ query: LOYALTY_PROGRAMS_QUERY }],
      });
      setEdit(null);
    } catch (e) {
    } finally {
      setLoading(false);
    }
  };

  const isValid = (config = {}) => {
    if (config.temporary && (!config.beginDate || !config.endDate)) return false;
    return Object.values(config).find(v => v === '') === undefined;
  };

  const getProgramProps = ({ loyaltyType, loyaltyConfig = {} }) => {
    switch (loyaltyType) {
      case 'DEFAULT_DISCOUNT':
        return {
          config: {
            amount: loyaltyConfig.discount || '',
          },
          title: t('Базовый процент скидки'),
          description: t('Устанавливается фиксированный размер скидки.'),
          amountTitle: t('Размер скидки {{amount}}%', { amount: loyaltyConfig.discount }),
          amountFormTitle: t('Размер скидки, %'),
          serialize: ({ amount }) => ({
            discount: +amount,
            loyaltyType,
            id: loyaltyConfig.id,
          }),
          percentFormatter: true,
          onDisable: () => {},
        };
      case 'BONUS_EARN':
        return {
          config: {
            expiration: loyaltyConfig.expiration || 0,
            amount: loyaltyConfig.percentage || '',
          },
          title: t('Бонусы с каждого заказа'),
          description: t('Начисляется процент от стоимости заказа в виде бонусов.'),
          serialize: ({ expiration, amount }) => ({
            expiration: +expiration,
            percentage: +amount,
            loyaltyType,
            id: loyaltyConfig.id,
          }),
          amountTitle: t('Размер скидки {{amount}}%', { amount: loyaltyConfig.percentage }),
          amountFormTitle: t('Размер бонусов от суммы заказа, %'),
          percentFormatter: true,
        };
      case 'BDAY_BONUSES':
        return {
          config: {
            amount: loyaltyConfig.bonuses || '',
            expiration: loyaltyConfig.expiration || 0,
          },
          title: t('Бонусы на день рождения'),
          description: t(
            'Начисляется фиксированное количество бонусов. Может быть равно стоимости каких-то определенных товаров/услуг.',
          ),
          amountTitle: t('Количество бонусов: {{amount}}', { amount: loyaltyConfig.bonuses }),
          amountFormTitle: t('Количество бонусов'),
          serialize: ({ amount, expiration }) => ({
            bonuses: +amount,
            expiration: +expiration,
            loyaltyType,
            id: loyaltyConfig.id,
          }),
        };
      case 'BDAY_DISCOUNT':
        return {
          config: {
            amount: loyaltyConfig.discount || '',
            period: loyaltyConfig.period || '',
          },
          title: t('Скидка на день рождения'),
          description: t('Дается процентная скидка на покупки в день рождения.'),
          amountTitle: t('Размер скидки {{amount}}%', { amount: loyaltyConfig.discount }),
          amountFormTitle: t('Размер скидки, %'),
          percentFormatter: true,
          serialize: ({ amount, period }) => ({
            discount: +amount,
            period: +period,
            loyaltyType,
            id: loyaltyConfig.id,
          }),
        };
      case 'WELCOME_BONUSES':
        return {
          config: {
            amount: loyaltyConfig.bonuses || '',
            expiration: loyaltyConfig.expiration || 0,
          },
          title: t('Бонусы при активации карты'),
          description: t('Начисляется фиксированное количество бонусов сразу после активации карты.'),
          amountTitle: t('Количество бонусов: {{amount}}', { amount: loyaltyConfig.bonuses }),
          amountFormTitle: t('Количество бонусов'),
          serialize: ({ amount, expiration }) => ({
            bonuses: +amount,
            expiration: +expiration,
            loyaltyType,
            id: loyaltyConfig.id,
          }),
        };
      case 'FIRST_ORDER_BONUSES':
        return {
          config: {
            amount: loyaltyConfig.bonuses || '',
            after: loyaltyConfig.activationAfter || '',
            expiration: loyaltyConfig.expiration || 0,
          },
          title: t('Бонусы через N дней после первого заказа'),
          description: t(
            'Увеличение процента повторных посещений: выдаётся фиксированный бонус, если клиент не совершал покупку дольше среднего времени для повторных покупок по клиентской базе.',
          ),
          amountTitle: t('Количество бонусов: {{amount}}', { amount: loyaltyConfig.bonuses }),
          amountFormTitle: t('Количество бонусов'),
          serialize: ({ amount, after, expiration }) => ({
            bonuses: +amount,
            activationAfter: +after,
            expiration: +expiration,
            loyaltyType,
            id: loyaltyConfig.id,
          }),
        };
      case 'LAST_ORDER_BONUSES':
        return {
          config: {
            amount: loyaltyConfig.bonuses || '',
            after: loyaltyConfig.activationAfter || '',
            expiration: loyaltyConfig.expiration || 0,
          },
          title: t('Бонусы через N дней от последнего заказа'),
          description: t(
            'Реактивация потерянных клиентов: выдаем фиксированный бонус, если клиент не совершал покупку долгое время.',
          ),
          amountTitle: t('Количество бонусов: {{amount}}', { amount: loyaltyConfig.bonuses }),
          amountFormTitle: t('Количество бонусов'),
          serialize: ({ amount, after, expiration }) => ({
            bonuses: +amount,
            activationAfter: +after,
            expiration: +expiration,
            loyaltyType,
            id: loyaltyConfig.id,
          }),
        };
      case 'BONUS_SPEND_LIMIT':
        return {
          config: {
            amount: loyaltyConfig.percentage || '',
          },
          title: t('Лимит для оплаты бонусами'),
          description: t(
            'Выставляется ограничение на размер доли, которую допустимо оплачивать с помощью бонусов. Не рекомендуется устанавливать ниже 30%.',
          ),
          amountTitle: t('Размер скидки {{amount}}%', { amount: loyaltyConfig.percentage }),
          amountFormTitle: t('Лимит от суммы заказа, %'),
          serialize: ({ amount }) => ({
            percentage: +amount,
            loyaltyType,
            id: loyaltyConfig.id,
          }),
          percentFormatter: true,
        };
      case 'NTH_ORDER_FREE':
        const baseConfig = {
          name: '',
          nthCount: '',
          useInCurrentOrder: true,
          useSeveralTimes: true,
          temporary: false,
        };
        return {
          type: 'NTH_ORDER_FREE',
          config: onlyData({ ...baseConfig, ...loyaltyConfig }),
          title: t('N заказ бесплатно'),
          name: loyaltyConfig.name,
          description: t('Следующая по счету покупка оформляется со 100% скидкой.'),
          serialize: config => ({
            ...config,
            nthCount: +config.nthCount,
            loyaltyType,
          }),
        };
      default:
        return {};
    }
  };

  const getCreateItems = () => {
    if (!data) return [];

    const uniq = [
      'BDAY_BONUSES',
      'BDAY_DISCOUNT',
      'BONUS_EARN',
      'BONUS_SPEND_LIMIT',
      'DEFAULT_DISCOUNT',
      'FIRST_ORDER_BONUSES',
      'LAST_ORDER_BONUSES',
      'WELCOME_BONUSES',
    ];
    const created = new Set(data.loyaltyPrograms.map(({ loyaltyType }) => loyaltyType));

    return uniq
      .filter(type => !created.has(type))
      .concat(['NTH_ORDER_FREE'])
      .map(loyaltyType => ({
        label: getProgramProps({ loyaltyType }).title,
        onClick: () => setEdit(getProgramProps({ loyaltyType })),
      }));
  };

  const setField = field => {
    setEdit({ ...edit, config: { ...edit.config, ...field } });
  };

  if (!data) return null;

  const { loyaltyPrograms } = data;
  const isEmpty = !loyaltyPrograms.length;

  return (
    <WithTitle title={t('Программа лояльности')}>
      <div className={s.header}>
        <h1>{t('Программа лояльности')}</h1>
        {!isEmpty && <Select theme="green" loading={false} label={t('Добавить программу')} items={getCreateItems()} />}
      </div>
      <main className={s.main}>
        {isEmpty ? (
          <>
            <p className={s.placeholder}>{t('Вы можете создавать и комбинировать программы лояльности')}</p>
            <Select theme="green" label={t('Добавить программу')} items={getCreateItems()} />
          </>
        ) : (
          loyaltyPrograms.map((props, i) => <Program setEdit={setEdit} key={i} {...getProgramProps(props)} />)
        )}
        <Modal title={edit?.title} isOpen={!!edit} onClose={() => setEdit(null)}>
          <Form className={s.form} onSubmit={onSave}>
            {edit?.type === 'NTH_ORDER_FREE' ? (
              <NthOrderForm setField={setField} {...edit} />
            ) : (
              <ProgramForm setField={setField} {...edit} />
            )}
            <div>
              <Button type="submit" fixedWidth={140} disabled={!isValid(edit?.config)} loading={loading} center>
                {t('Сохранить')}
              </Button>
              <Button theme="black" onClick={() => setEdit(null)} fixedWidth={140} center>
                {t('Отмена')}
              </Button>
            </div>
          </Form>
        </Modal>
      </main>
    </WithTitle>
  );
};

export default Loyalty;
